import React, {
    useEffect,
    useState,
    useRef,
    ReactNode
} from 'react';

// Material ui
import { Grid } from '@material-ui/core';

// Components
import SelectInput, { SelectOption } from '@components/UI-KIT/Inputs/SelectInput';
import Button from '@components/UI-KIT/Button';
import Footer from '@components/UI-KIT/Footer';
import AmountFormat from '@components/utils/AmountFormat';
import Checkbox from '@/src/components/UI-KIT/Checkbox';

// Types
import { TransactionDto, TransactionBetweenAccountsDto } from '@services/account-transaction';
import { Account } from '@services/account';
import { Concept } from '@services/concept';
import { Contract } from '@services/contract';
import { Tenant } from '@services/tenant';
import { Property } from '@services/property';

// Icons
import {
    LeftArrow,
    Outgoing,
    Income,
    MovementOutlined
} from '@/assets/icons';

// Styles
import VARIABLES from '@/assets/variables';

interface Props {
    transactionType: string;
    transaction: TransactionDto;
    transactionBetween: TransactionBetweenAccountsDto;
    setTransactionBetween: React.Dispatch<React.SetStateAction<TransactionBetweenAccountsDto>>;
    setActiveStep: React.Dispatch<React.SetStateAction<number>>;
    setNumberOfSteps: React.Dispatch<React.SetStateAction<number>>;
    setTransaction: React.Dispatch<React.SetStateAction<TransactionDto>>;
    accounts: SelectOption[];
    concepts: SelectOption[];
    tenantSelectOptions: SelectOption[];
    propietorSelectOptions: SelectOption[];
    propertySelectOptions: SelectOption[];
    setDrawer: React.Dispatch<React.SetStateAction<{
        drawerOpened: boolean;
        drawerTitle: ReactNode | string;
        drawerChildren: ReactNode;
        drawerFooter: boolean;
        titleIcon: ReactNode;
    }>>;
    contracts: Contract[];
}

const SecondStep: React.FC<Props> = ({
    transactionType,
    transaction,
    setTransactionBetween,
    transactionBetween,
    setActiveStep,
    setTransaction,
    setNumberOfSteps,
    accounts,
    concepts,
    contracts,
    tenantSelectOptions,
    propietorSelectOptions,
    propertySelectOptions,
    setDrawer
}) => {
    const [ switchDisabled, setSwitchDisabled ] = useState(true);
    const associatedToOptions = useRef([
        {
            label: 'Inquilino',
            value: 'Tenant'
        },
        {
            label: 'Propietario',
            value: 'Propietor'
        },
        {
            label: 'Administración',
            value: 'Administrator'
        }
    ]);
    const associatedRef = useRef(transaction.associatedTo);

    const cleanValues = () => {
        setActiveStep(prevState => prevState - 1);
        setTransaction(prevState => ({
            ...prevState,
            account: null,
            concept: null,
            associatedTo: null,
            property: null,
            associatedTenant: null,
            associatedPropietor: null
        }));
    };

    useEffect(() => {
        setDrawer(prevState => ({
            ...prevState,
            drawerTitle: (
                <>
                    {`${transactionType === 'Income'
                        ? 'Ingreso de'
                        : transactionType === 'Outgoing'
                            ? 'Egreso de'
                            : 'Movimiento por'}
                         ARS`}
                    {'\xa0'}
                    <AmountFormat
                        value={transaction.amount}
                    />
                </>
            ),
            titleIcon: transactionType === 'Income'
                ? <Income />
                : transactionType === 'Outgoing'
                    ? <Outgoing />
                    : <MovementOutlined />
        }));
    }, [ ]);

    useEffect(() => {
        if (transaction.associatedTo === associatedRef.current) return;
        setTransaction(prevState => ({
            ...prevState,
            associatedTenant: null,
            associatedPropietor: null,
            includeBilling: false
        }));
        associatedRef.current = transaction.associatedTo;
    }, [ transaction.associatedTo ]);

    useEffect(() => {
        setSwitchDisabled(true);
        if (transaction.associatedTo === 'Tenant') {
            let tenantHasContract = false;
            contracts.forEach((contract: Contract) => {
                contract.tenants.some((tenant: Tenant) => tenant.id === transaction.associatedTenant)
                    && (tenantHasContract = true);
            });
            setTransaction(prevState => ({
                ...prevState,
                includeBilling: tenantHasContract
            }));
            setSwitchDisabled(!tenantHasContract);

        }
    }, [ transaction.associatedTenant ]);

    useEffect(() => {
        if (transaction.includeBilling) setNumberOfSteps(5);
        else setNumberOfSteps(4);
    }, [ transaction.includeBilling ]);

    return (
        <>
            { [ 'Income', 'Outgoing' ].includes(transactionType) && (
                <>
                    <SelectInput
                        label="Cuenta"
                        placeholder="Selecciona una opción.."
                        value={accounts.filter(({ value }) => value.id === transaction.account)[0]}
                        onChange={(account: Account) => setTransaction(prevState => ({
                            ...prevState,
                            account: account?.id
                        }))}
                        options={accounts}
                    />
                    <SelectInput
                        label="Concepto"
                        placeholder="Selecciona una opción.."
                        value={concepts.find(({ value }) => value.id === transaction.concept)}
                        onChange={(concept: Concept) => setTransaction(prevState => ({
                            ...prevState,
                            concept: concept?.id
                        }))}
                        options={concepts}
                    />
                    <SelectInput
                        label="Asociado a"
                        placeholder="Selecciona una opción.."
                        value={transaction.associatedTo ? associatedToOptions.current.find(option => transaction.associatedTo === option.value) : null}
                        onChange={(associatedTo: 'Tenant' | 'Propietor' | null) => {
                            setTransaction(prevState => ({
                                ...prevState,
                                associatedTo,
                                imputation: associatedTo,
                                property: null,
                                includeBilling: false
                            }));
                            setNumberOfSteps(4);
                        }}
                        options={associatedToOptions.current}
                    />
                    { transaction.associatedTo === 'Tenant' && (
                        <SelectInput
                            placeholder="Selecciona un inquilino"
                            value={tenantSelectOptions.find(({ value }: SelectOption) => value.id === transaction.associatedTenant)}
                            onChange={(person: any) => {
                                setTransaction(prevState => ({
                                    ...prevState,
                                    associatedTenant: person ? person.id : false
                                }));
                            }}
                            options={tenantSelectOptions}
                        />
                    )}
                    { transaction.associatedTo === 'Propietor' && (
                        <>
                            <SelectInput
                                placeholder="Selecciona un propietario"
                                value={propietorSelectOptions.find(({ value }: SelectOption) => value.id === transaction.associatedPropietor)}
                                onChange={(person: any) => {
                                    setTransaction(prevState => ({
                                        ...prevState,
                                        associatedPropietor: person ? person.id : null,
                                        property: null
                                    }));
                                }}
                                options={propietorSelectOptions}
                            />
                            <SelectInput
                                optional
                                label="Propiedad"
                                disabled={!transaction.associatedPropietor}
                                value={propertySelectOptions.find(({ value }: SelectOption) => value.id === transaction.property)}
                                onChange={(property: Property) => {
                                    setTransaction(prevState => ({
                                        ...prevState,
                                        property: property ? property.id : null
                                    }));
                                }}
                                options={propertySelectOptions.filter(({ value }: { value: Property }) => value.propietors[0].id === transaction.associatedPropietor)}
                            />
                        </>
                    )}
                    { [ 'Tenant' ].includes(transaction.associatedTo) && (
                        <Checkbox
                            variant="switch"
                            disabled={switchDisabled}
                            label="Incluir en cobro de alquiler (como nuevo concepto)"
                            checked={transaction.includeBilling}
                            onChange={() => {
                                setTransaction(prevState => ({
                                    ...prevState,
                                    includeBilling: !prevState.includeBilling
                                }));
                            }}
                        />
                    )}
                </>
            )}
            { transactionType === 'Movement' && (
                <>
                    <SelectInput
                        label="Cuenta origen"
                        placeholder="Selecciona una opción.."
                        value={transactionBetween.rootAccount}
                        onChange={(account: Account) => setTransactionBetween(prevState => ({
                            ...prevState,
                            rootAccount: account?.id
                        }))}
                        options={accounts.filter(({ value }: SelectOption) => value.id !== transactionBetween.destinationAccount)}
                    />
                    <SelectInput
                        label="Cuenta destino"
                        placeholder="Selecciona una opción.."
                        value={transactionBetween.destinationAccount}
                        onChange={(account: Account) => setTransactionBetween(prevState => ({
                            ...prevState,
                            destinationAccount: account?.id
                        }))}
                        options={accounts.filter(({ value }: SelectOption) => value.id !== transactionBetween.rootAccount)}
                    />
                </>
            )}
            <Footer
                className="AOL-Account-stepper-footer"
            >
                <Grid
                    container
                    justify="space-between"
                >
                    <Button
                        label="Anterior"
                        level={3}
                        icon={(
                            <LeftArrow
                                color={VARIABLES.palette.secondary_color}
                            />
                        )}
                        onClick={() => cleanValues()}
                    />
                    <Button
                        label="Siguiente"
                        disabled={transactionType !== 'Movement'
                            ? !transaction.account
                                || !transaction.concept
                                || !transaction.associatedTo
                                || !(
                                    transaction.associatedTenant
                                        || transaction.associatedPropietor
                                        || transaction.associatedTo === 'Administrator'
                                )
                            : !transactionBetween.rootAccount
                                || !transactionBetween.destinationAccount
                        }
                        onClick={() => setActiveStep(prevState => prevState + 1)}
                    />
                </Grid>
            </Footer>
        </>
    );
};

export default SecondStep;